import Datepicker from '@components/Datepicker'
import RightSidebarFloat from '@components/RightSidebarFloat'
import WithUserTime, { WithUserTimeProps } from '@hocs/WithUserTime'
import { DATE_FORMAT } from '@utils/constants'
import filter from 'lodash/filter'
import groupBy from 'lodash/groupBy'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Col, Row } from 'react-styled-flexboxgrid'
import { SessionInfo } from '../ScheduleListCard/interface'
import { StyledCalendar, StyledCalendarLoader, StyledCalendarLoaderBG, StyledCol } from './style'
import { DotPulse } from '@berlitzplatforms/micro.ui.loading-dots'
import { useApolloClient } from '@apollo/react-hooks'
import { IGetEventAttendee, IGetEventAttendeeData, IGetEventsInput } from '@interfaces/Events'
import { GET_EVENT_ATTENDEE } from '@queries/events'
import PortalRegistrations from '@classes/Registrations'
import { useSelector } from 'react-redux'
import { StoreProps } from '@interfaces/StoreState'
import { utcfyDate } from '@layouts/Events/helpers'
import { usePortalFeatures } from '@components/RoleBasedView'
import { ATTENDEE_STATUS } from '@utils/constants/events'
import { ApolloQueryResult } from 'apollo-client'
import { flatten } from 'lodash'

interface MyCalendarSideBar {
  onMonthChange: Function
  onPicker: Function
  onPickerSelect: Function
  openToDate: Date
  availableSchedules: SessionInfo[]
  loading: Boolean
  selected?: Date | null
  variables: {
    FromDate?: string
    Id?: string
    EndDate?: string
    FilterType?: string
    Limit?: number
    UserId?: string
  }
}

const MyCalendarSideBar: React.FC<MyCalendarSideBar & WithUserTimeProps> = ({
  onMonthChange,
  availableSchedules,
  onPicker,
  openToDate,
  loading,
  onPickerSelect,
  selected,
  toUserDate,
  variables,
}) => {
  const { state } = usePortalFeatures({
    configId: 'c::sidenav::events',
  })
  const client = useApolloClient()
  const { Type, Registrations } = useSelector(({ userProfile }: StoreProps) => {
    const { Type } = userProfile.info
    const Registrations = new PortalRegistrations(
      userProfile.info?.StudentProfile?.StudentProfileInfo?.RegistrationWrappers
    )

    return {
      Registrations,
      Type,
    }
  })

  const [eventLoading, setEventLoading] = useState(false)
  const [userEvents, setUserEvents] = useState<IGetEventAttendee[]>([])

  const getUserEvents = async (defaultPayload: IGetEventsInput) => {
    setEventLoading(true)

    const promises: Promise<ApolloQueryResult<IGetEventAttendeeData>>[] = await (
      Registrations?.getEventAtteendeesPayload(defaultPayload) || []
    )?.map((variables) =>
      client.query({
        query: GET_EVENT_ATTENDEE,
        fetchPolicy: 'network-only',
        variables,
      })
    )

    const res: ApolloQueryResult<IGetEventAttendeeData>[] = await Promise.all(promises)
    setEventLoading(false)
    setUserEvents(flatten((res || [])?.map(({ data }) => data?.getEventAttendeev2)))
  }

  useEffect(() => {
    let unmounted = false

    if (!unmounted && state.allowed) {
      getUserEvents({
        StartDateTime: utcfyDate(moment(variables?.FromDate || '')),
        EndDateTime: utcfyDate(moment(variables?.EndDate || '')),
        RecordId: '',
        Type,
      })
    }

    return () => {
      unmounted = true
    }
  }, [state.allowed, variables?.FromDate, variables?.EndDate, Type])

  const highlightedDates = []

  const groupedByDate = groupBy(
    filter(availableSchedules, ({ Status }) => Status !== 'Cancelled'),
    (el) => toUserDate(el.StartTime, DATE_FORMAT.TZ, DATE_FORMAT.BASIC)
  )
  const groupedEventByDate = state.allowed
    ? groupBy(
        (userEvents || []).filter(
          ({ berlitzEventAttendeeInfo }) => berlitzEventAttendeeInfo.AttendeeStatus !== ATTENDEE_STATUS.CANCELLED
        ),
        (el) => toUserDate(el.berlitzEventAttendeeInfo.StartDatetime, DATE_FORMAT.TZ, DATE_FORMAT.BASIC)
      )
    : {}

  Object.keys({ ...groupedByDate, ...groupedEventByDate }).forEach((date) => {
    const classname = `react-datepicker__day--highlighted-count-${
      [...(groupedByDate[date] || []), ...(groupedEventByDate[date] || [])].length
    } e2e-highlighted`
    if (!highlightedDates[classname]) {
      highlightedDates[classname] = []
    }
    highlightedDates[classname].push(moment(date, 'YYYY-MM-DD').toDate())
  })

  const formedHighlightedDates = Object.keys(highlightedDates).map((highlight) => ({
    [highlight]: highlightedDates[highlight],
  }))

  const header = (
    <>
      <StyledCalendar />{' '}
      <span>
        <FormattedMessage id="My calendar" />
      </span>
    </>
  )
  const body = (
    <Row>
      <StyledCol xs={12}>
        {(loading || eventLoading) && (
          <>
            <StyledCalendarLoaderBG />
            <StyledCalendarLoader>
              <DotPulse />
            </StyledCalendarLoader>
          </>
        )}
        <Datepicker
          onChange={onPicker}
          onSelect={onPickerSelect}
          selected={selected}
          onMonthChange={onMonthChange}
          borderless
          inline
          highlightDates={formedHighlightedDates}
          openToDate={openToDate}
        />
      </StyledCol>
      <Col xs={false} sm={false} md={false} lg={12}>
        {/* Need to comment for now. Reference: https://berlitz.atlassian.net/browse/PTL-331 */}
        {/* <Download>
          <DownloadCalendar />
          <FormattedMessage id="Download to calendar" />
        </Download> */}
      </Col>
    </Row>
  )
  return <RightSidebarFloat hideHeaderOnLg header={header} body={body} />
}

export default WithUserTime(MyCalendarSideBar)
